/**
 * プレイスラベルの2段目に出す「表示項目の値」と「アイコン」を求める。
 *
 * 表示項目の種別とレスポンスの形は管理画面側の設計（IFIELD_V4-7149 基本設計V2）に合わせている。
 *
 * | 種別 | 値の入り方 | アイコン |
 * |---|---|---|
 * | プレイスコード / 自動チェックイン範囲 / 電話番号 / FAX番号 | 種別と同じ名前のフィールド | 無し |
 * | 屋内（ビル・フロア） | ユーザーの任意項目と同じスキーマ | ユーザー版の建物アイコン |
 * | プレイス属性 | 同上 | 無し（ユーザー属性と同じ扱い） |
 * | センサー値 | 同上 | ユーザー版のセンサーアイコン |
 * | 紐付けセンサー名 | 同上 | 無し（ユーザーのカードと同じ扱い） |
 *
 * 共通スキーマの4種類は**ユーザー側と同じ処理をそのまま通す**。
 * 二重実装にすると単位や括弧の付け方がリストとずれるため。
 */

import {
  PLACE_ITEM_TYPE,
  getPlaceItemDefaultValue,
  withPlaceItemUnit,
} from '@/js/util/PlaceLabelUtil.js'
import { getItemText, getItemType } from '@/js/util/UserLabelItemText.js'

/**
 * 値が「種別と同じ名前のフィールド」に入る項目
 * 例: `{ item_type: 'place_code', place_code: '1234' }`
 * @type {Array.<String>}
 */
const VALUE_FIELD_ITEM_TYPES = [
  PLACE_ITEM_TYPE.PLACE_CODE,
  PLACE_ITEM_TYPE.CHECKIN_RADIUS,
  PLACE_ITEM_TYPE.TEL,
  PLACE_ITEM_TYPE.FAX,
]

/**
 * 表示項目とアイコンの対応
 *
 * プレイス固有の項目（プレイスコード・自動チェックイン範囲・電話番号・FAX番号）は
 * 専用のアイコンを持たないため、テキストだけを出す
 * @type {Object.<String, String>}
 */
const ITEM_ICON = {
  [PLACE_ITEM_TYPE.INDOOR]: 'building',
  [PLACE_ITEM_TYPE.SENSOR_DATA]: 'sensor',
}

/**
 * 表示項目のアイコン名を返す
 * @param {Object} item 表示項目の値
 * @returns {String|null} アイコン名
 */
export const getPlaceItemIcon = (item) => ITEM_ICON[getItemType(item)] || null

/**
 * 表示項目の値を1行の文字列にする
 * @param {Object} item 表示項目の値
 * @param {Object} [place] プレイス（値が無いときの既定値を求めるのに使う）
 * @returns {String} 表示する文字列
 */
export const getPlaceItemText = (item, place) => {
  const type = getItemType(item)
  if (VALUE_FIELD_ITEM_TYPES.includes(type)) {
    // 単位はリストのカードと同じものを付ける（IFIELD_V4-7276）。
    // 項目名はラベルの幅に収めるため付けない
    const value = item?.[type] ?? getPlaceItemDefaultValue(type, place)
    return withPlaceItemUnit(type, value)
  }
  // 屋内・プレイス属性・センサー値はユーザーの任意項目と同じスキーマ
  return getItemText(item)
}
